/**
 * PerformanceMonitor - Real-time performance tracking and optimization
 * 
 * Monitors rendering performance, frame rates, and system metrics
 * to ensure optimal document editing experience.
 */

interface PerformanceMetrics {
  operationTime: number;
  renderTime: number;
  memoryUsage: number;
  syncLatency: number;
  frameRate: number;
}

export class PerformanceMonitor {
  private metrics: PerformanceMetrics;
  private frameRateHistory: number[] = [];
  private renderTimeHistory: number[] = [];
  private lastFrameTime: number = 0;

  constructor() {
    this.metrics = {
      operationTime: 0,
      renderTime: 0,
      memoryUsage: 0,
      syncLatency: 0,
      frameRate: 60
    };
    
    this.startMonitoring();
  }

  private startMonitoring(): void {
    // Monitor frame rate
    const trackFrame = () => {
      const now = performance.now();
      if (this.lastFrameTime > 0) {
        const frameTime = now - this.lastFrameTime;
        const fps = 1000 / frameTime;
        this.updateFrameRate(fps);
      }
      this.lastFrameTime = now;
      requestAnimationFrame(trackFrame);
    };
    
    requestAnimationFrame(trackFrame);
  }

  private updateFrameRate(fps: number): void {
    this.frameRateHistory.push(fps);
    if (this.frameRateHistory.length > 60) {
      this.frameRateHistory.shift();
    }
    
    this.metrics.frameRate = this.frameRateHistory.reduce((sum, val) => sum + val, 0) / this.frameRateHistory.length;
  }

  getCurrentFrameRate(): number {
    return this.metrics.frameRate;
  }

  getAverageRenderTime(): number {
    return this.metrics.renderTime;
  }

  updateRenderTime(renderTime: number): void {
    this.renderTimeHistory.push(renderTime);
    if (this.renderTimeHistory.length > 60) {
      this.renderTimeHistory.shift();
    }
    
    this.metrics.renderTime = this.renderTimeHistory.reduce((sum, val) => sum + val, 0) / this.renderTimeHistory.length;
  }

  recordOperation(type: string, time: number): void {
    // Record operation performance
    console.log(`[PerformanceMonitor] ${type} operation: ${time.toFixed(2)}ms`);
  }

  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  destroy(): void {
    this.frameRateHistory = [];
    this.renderTimeHistory = [];
  }
}
